import { Button, Tooltip, notification, Modal, Select } from 'antd';
import { DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import type { Key } from 'react';
import {
  ControlRow,
  SelectOption,
  emptyFilledFilters
} from 'store/ducks/InternalControls/types';
import CustomSelect from 'components/CustomSelect';
import dayjs from 'dayjs';
import CustomDatePicker from 'components/CustomDatePicker';
import Helpers from 'utils/Helpers';
import {
  instance_fields_control,
  base_fields_control
} from './riskControlFieldsToDisable';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import * as Styled from '../pages/InternalControlsMain/RiskControl.styled';
import * as ControlStyled from '../pages/InternalControlsMain/InternalControls/InternalControls.styled';

export const getControlColumns = (
  owner: SelectOption[],
  controlTypes: SelectOption[],
  controls: ControlRow[],
  controlNature: SelectOption[],
  controlFrequency: SelectOption[],
  controlPerformer: SelectOption[],
  controlStatus: SelectOption[],
  controlOrigin: SelectOption[],
  controlCustodian: SelectOption[],
  reasonForControlStatus: SelectOption[],
  setControls: (
    controls: ControlRow[] | ((prev: ControlRow[]) => ControlRow[])
  ) => void,
  newControlIds?: Set<number>,
  hasMainOrganization?: boolean,
  controlPrerequisites?: SelectOption[],
  t?: (key: string) => string,
  onDelete?: (id: number) => void
): ColumnsType<ControlRow> => {
  const shouldDisable = (record: ControlRow, fieldKey: string) => {
    if (record.notUsed) return true;

    if (record.isCopied) {
      if (record.instance_id === 0) {
        return instance_fields_control.has(fieldKey);
      }
      return base_fields_control.has(fieldKey);
    }

    if (record.isNew) {
      return instance_fields_control.has(fieldKey);
    }

    if (hasMainOrganization) {
      return instance_fields_control.has(fieldKey);
    }
    return base_fields_control.has(fieldKey);
  };

  const handleDuplicateRiskRow = (record: ControlRow) => {
    const newId = generateUniqueId();

    const index = controls.findIndex((r) => r.id === record.id);

    const duplicatedRow: ControlRow = {
      ...structuredClone(record),
      id: newId,
      isNew: true,
      isCopied: true
    };

    const newControls = [...controls];
    newControls.splice(index + 1, 0, duplicatedRow);

    setControls(newControls);

    if (newControlIds) {
      newControlIds.add(newId);
    }
  };

  return [
    {
      title: t ? t('ACTIONS') : 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'left',
      render: (_, record) => (
        <>
          <Styled.CopyIcon
            onClick={() => handleDuplicateRiskRow(record)}
            icon={<CopyOutlined />}
            style={{ marginRight: 8 }}
          />
          <Button
            type='link'
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              if (newControlIds?.has(record.id)) {
                setControls(controls.filter((c) => c !== record));
                return;
              }
              onDelete?.(record.id);
            }}
          />
        </>
      )
    },
    {
      title: t ? t('CONTROL_NAME') : 'Control Name',
      dataIndex: 'controlName',
      key: 'controlName',
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlName'),
      width: 200,
      fixed: 'left',
      ellipsis: true,
      render: (value: string, record, index) => {
        if (record.notUsed) {
          return (
            <Tooltip title='Disabled due to a previous Risk/Control type change.'>
              <Styled.DisabledTooltipDiv>
                {value || '—'}
              </Styled.DisabledTooltipDiv>
            </Tooltip>
          );
        }
        return (
          <>
            <Styled.GlobalTooltipStyles />
            <Tooltip
              title={
                value
                  ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                  : ''
              }
              placement='topLeft'
              mouseEnterDelay={0.1}
              mouseLeaveDelay={0.1}
              arrow={false}
            >
              <Styled.ResizableTextArea
                value={value || ''}
                placeholder='Enter control name'
                disabled={shouldDisable(record, 'controlName')}
                onChange={(e) => {
                  const newControls = [...controls];
                  const actualIndex = newControls.findIndex(
                    (c) => c.id === record.id
                  );
                  if (actualIndex !== -1) {
                    newControls[actualIndex]._pinned = true;
                    newControls[actualIndex].controlName = e.target.value;
                    setControls(newControls);
                  }
                }}
              />
            </Tooltip>
          </>
        );
      }
    },
    {
      title: t ? t('CONTROL_DESCRIPTION') : 'Control Description',
      dataIndex: 'controlDescription',
      key: 'controlDescription',
      width: 400,
      fixed: 'left',
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlDescription'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              value
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value || ''}
              placeholder='Enter control description'
              rows={3}
              disabled={shouldDisable(record, 'controlDescription')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex]._pinned = true;
                  newControls[actualIndex].controlDescription = e.target.value;
                  setControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t ? t('OWNER_FIRST_LINE') : 'Owner (1st line)',
      dataIndex: 'owner',
      key: 'owner',
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('owner'),
      width: 150,
      ellipsis: true,
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={owner}
          placeholder='Select Owner'
          disabled={shouldDisable(record, 'owner')}
          onChange={(id: number) => {
            const selected = owner.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex]._pinned = true;
              newControls[actualIndex].owner = {
                id,
                name: selected?.label ?? null
              };
              setControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t
        ? t('CONTROL_PERFORMER_FIRST_LINE')
        : 'Control performer (1st line)',
      dataIndex: 'controlPerformer',
      key: 'controlPerformer',
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlPerformer'),
      width: 200,
      ellipsis: true,
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={controlPerformer}
          placeholder='Select Control Performer'
          disabled={shouldDisable(record, 'controlPerformer')}
          onChange={(id: number) => {
            const selected = controlPerformer.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex]._pinned = true;
              newControls[actualIndex].controlPerformer = {
                id,
                name: selected?.label ?? null
              };
              setControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t ? t('CONTROL_ORIGIN_FIRST_LINE') : 'Control origin (1st line)',
      dataIndex: 'controlOrigin',
      key: 'controlOrigin',
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlOrigin'),
      width: 200,
      ellipsis: true,
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={controlOrigin}
          placeholder='Select Control origin'
          disabled={shouldDisable(record, 'controlOrigin')}
          onChange={(id: number) => {
            const selected = controlOrigin.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex]._pinned = true;
              newControls[actualIndex].controlOrigin = {
                id,
                name: selected?.label ?? null
              };
              setControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t ? t('CONTROL_STATUS_FIRST_LINE') : 'Control Status (1st line)',
      dataIndex: 'controlStatus',
      key: 'controlStatus',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlStatus'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={controlStatus}
          placeholder='Select Control Status'
          disabled={shouldDisable(record, 'controlStatus')}
          onChange={(id: number) => {
            const selected = controlStatus.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex]._pinned = true;
              newControls[actualIndex].controlStatus = {
                id,
                name: selected?.label ?? null
              };
              setControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t
        ? t('REASON_FOR_CONTROL_STATUS_FIRST_LINE')
        : 'Reason for control status(1st line)',
      dataIndex: 'reasonForControlStatus',
      key: 'reasonForControlStatus',
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>(
        'reasonForControlStatus'
      ),
      width: 200,
      ellipsis: true,
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={reasonForControlStatus}
          placeholder='Select reason for control status'
          disabled={shouldDisable(record, 'reasonForControlStatus')}
          onChange={(id: number) => {
            const selected = reasonForControlStatus.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex]._pinned = true;
              newControls[actualIndex].reasonForControlStatus = {
                id,
                name: selected?.label ?? null
              };
              setControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t ? t('CONTROL_PREREQUISITES') : 'Control Prerequisites',
      dataIndex: 'controlPrerequisites',
      key: 'controlPrerequisites',
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlPrerequisites'),
      width: 250,
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={controlPrerequisites || []}
          placeholder='Select prerequisites'
          disabled={shouldDisable(record, 'controlPrerequisites')}
          onChange={(id: number) => {
            const selected = controlPrerequisites?.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex]._pinned = true;
              newControls[actualIndex].controlPrerequisites = {
                id,
                name: selected?.label ?? null
              };
              setControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t
        ? t('CONTROL_OVERALL_EFFECTIVENESS_FIRST_LINE')
        : 'Control Overall Effectiveness (1st line)',
      dataIndex: 'controlOverallEffectiveness',
      key: 'controlOverallEffectiveness',
      width: 220,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>(
        'controlOverallEffectiveness'
      ),
      render: (value, record, index) => (
        <div style={ControlStyled.readOnlyFieldStyle}>{value?.name || ''}</div>
      )
    },
    {
      title: t ? t('KEY_CONTROL') : 'Key Control',
      dataIndex: 'keyControl',
      key: 'keyControl',
      width: 120,
      ellipsis: true,
      render: (value: boolean, record, index) => (
        <Select
          value={value ? 'true' : 'false'}
          disabled={shouldDisable(record, 'keyControl')}
          style={ControlStyled.keyControlSelectStyle}
          onChange={(selectedValue: string) => {
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex].keyControl = selectedValue === 'true';
              setControls(newControls);
            }
          }}
        >
          <Select.Option value='true'>true</Select.Option>
          <Select.Option value='false'>false</Select.Option>
        </Select>
      )
    },
    {
      title: t
        ? t('CONTROL_OBJECTIVE_FIRST_LINE')
        : 'Control Objective (1st line)',
      dataIndex: 'controlObjective',
      key: 'controlObjective',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlObjective'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              value
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value || ''}
              placeholder='Enter control objective'
              rows={2}
              disabled={shouldDisable(record, 'controlObjective')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex]._pinned = true;
                  newControls[actualIndex].controlObjective = e.target.value;
                  setControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t ? t('CONTROL_TYPE_FIRST_LINE') : 'Control Type (1st line)',
      dataIndex: 'controlType',
      key: 'controlType',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlType'),
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={controlTypes}
          placeholder='Select control type'
          disabled={shouldDisable(record, 'controlType')}
          onChange={(id: number) => {
            const selected = controlTypes.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex]._pinned = true;
              newControls[actualIndex].controlType = {
                id,
                name: selected?.label ?? null
              };
              setControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t ? t('CONTROL_NATURE_FIRST_LINE') : 'Control Nature (1st line)',
      dataIndex: 'controlNature',
      key: 'controlNature',
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlNature'),
      width: 200,
      ellipsis: true,
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={controlNature}
          placeholder='Select control nature'
          disabled={shouldDisable(record, 'controlNature')}
          onChange={(id: number) => {
            const selected = controlNature.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex]._pinned = true;
              newControls[actualIndex].controlNature = {
                id,
                name: selected?.label ?? null
              };
              setControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t
        ? t('CONTROL_FREQUENCY_FIRST_LINE')
        : 'Control Frequency (1st line)',
      dataIndex: 'controlFrequency',
      key: 'controlFrequency',
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlFrequency'),
      width: 200,
      ellipsis: true,
      render: (value, record, index) => (
        <CustomSelect
          value={value?.id ?? undefined}
          options={controlFrequency}
          placeholder='Select control frequency'
          disabled={shouldDisable(record, 'controlFrequency')}
          onChange={(id: number) => {
            const selected = controlFrequency.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex]._pinned = true;
              newControls[actualIndex].controlFrequency = {
                id,
                name: selected?.label ?? null
              };
              setControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: (
        <ControlStyled.ControlValidityHeaderWrapper className='control-validity-header'>
          <ControlStyled.HeaderWithSeparator>
            {t ? t('CONTROL_VALIDITY_FROM_TO') : 'Control validity from - to'}
            <ControlStyled.HeaderSeparatorLine />
          </ControlStyled.HeaderWithSeparator>
          <ControlStyled.ControlValiditySeparatorLine />
        </ControlStyled.ControlValidityHeaderWrapper>
      ),
      onHeaderCell: () => ({
        style: ControlStyled.controlValidityHeaderStyle
      }),
      children: [
        {
          title: '',
          dataIndex: 'startDate',
          key: 'startDate',
          width: 150,
          onHeaderCell: () => ({
            style: ControlStyled.headerCellNoBorder
          }),
          onCell: () => ({
            style: ControlStyled.startDateCellStyle
          }),
          render: (value, record, index) => (
            <CustomDatePicker
              value={value ? dayjs(value) : null}
              placeholder='Validity from'
              style={ControlStyled.datePickerStyle}
              size='small'
              disabled={shouldDisable(record, 'startDate')}
              onChange={(date: dayjs.Dayjs | null) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );

                if (actualIndex !== -1) {
                  newControls[actualIndex].startDate = date
                    ? date.format('YYYY-MM-DD')
                    : null;

                  setControls(newControls);
                }
              }}
            />
          )
        },
        {
          title: '',
          dataIndex: 'endDate',
          key: 'endDate',
          width: 150,
          onHeaderCell: () => ({
            style: ControlStyled.headerCellNoBorder
          }),
          onCell: () => ({
            style: ControlStyled.endDateCellStyle
          }),
          render: (value, record, index) => (
            <CustomDatePicker
              value={value ? dayjs(value) : null}
              placeholder='Validity from'
              style={ControlStyled.datePickerStyle}
              size='small'
              disabled={shouldDisable(record, 'endDate')}
              onChange={(date: dayjs.Dayjs | null) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );

                if (actualIndex !== -1) {
                  newControls[actualIndex].endDate = date
                    ? date.format('YYYY-MM-DD')
                    : null;

                  setControls(newControls);
                }
              }}
            />
          )
        }
      ]
    },
    {
      title: t ? t('CONTROL_CUSTODIAN_GENERAL') : 'Control custodian (general)',
      dataIndex: 'controlCustodian',
      key: 'controlCustodian',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('controlCustodian'),
      render: (custodianValue, record, index) => (
        <CustomSelect
          value={Number(custodianValue?.id) || undefined}
          options={controlCustodian}
          disabled={shouldDisable(record, 'controlCustodian')}
          placeholder='Select Control custodian'
          onChange={(id: number) => {
            const selected = controlCustodian.find((o) => o.value === id);
            const newControls = [...controls];
            const actualIndex = newControls.findIndex(
              (c) => c.id === record.id
            );
            if (actualIndex !== -1) {
              newControls[actualIndex]._pinned = true;
              newControls[actualIndex].controlCustodian = {
                id: Number(id),
                name: selected?.label ?? ''
              };
              setControls(newControls);
            }
          }}
        />
      )
    },
    {
      title: t ? t('MONITORING_LEVEL') : 'Monitoring level',
      dataIndex: 'monitoringlevel',
      key: 'monitoringlevel',
      width: 200,
      ellipsis: true,
      filters: emptyFilledFilters,
      onFilter: Helpers.emptyFilledOnFilter<ControlRow>('monitoringlevel'),
      render: (value: string, record, index) => (
        <>
          <Styled.GlobalTooltipStyles />
          <Tooltip
            title={
              value
                ? value.substring(0, 50) + (value.length > 50 ? '...' : '')
                : ''
            }
            placement='topLeft'
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={false}
          >
            <Styled.ResizableTextArea
              value={value || ''}
              placeholder='Enter monitoring level'
              rows={3}
              disabled={shouldDisable(record, 'monitoringlevel')}
              onChange={(e) => {
                const newControls = [...controls];
                const actualIndex = newControls.findIndex(
                  (c) => c.id === record.id
                );
                if (actualIndex !== -1) {
                  newControls[actualIndex]._pinned = true;
                  newControls[actualIndex].monitoringlevel = e.target.value;
                  setControls(newControls);
                }
              }}
            />
          </Tooltip>
        </>
      )
    },
    {
      title: t ? t('Not used') : 'Not Used',
      dataIndex: 'notUsed',
      key: 'notUsed',
      width: 120,
      ellipsis: true,
      filters: [
        { text: 'true', value: true },
        { text: 'false', value: false }
      ],
      onFilter: (value: boolean | Key, record: ControlRow) =>
        record.notUsed === (value as unknown as boolean),
      render: (value: boolean) => <span>{String(value)}</span>
    }
  ];
};
